import {ReactNode, useState} from 'react';
import {FileWithPath} from 'react-dropzone';
import {
  Controller,
  FormProvider,
  Resolver,
  useForm,
  useWatch,
} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import useUpdateEffect from 'ahooks/es/useUpdateEffect';
import {isAxiosError} from 'axios';
import isNil from 'lodash/isNil';
import {useSnackbar} from 'notistack';
import {mixed, object, string} from 'yup';

import {
  Category,
  CategorySelect,
  SubCategory,
  SubCategorySelect,
} from '@entities/category';
import {GeoFilterOption, useGetGeoFilterOptions} from '@entities/common';
import {
  EventDisplayType,
  formatEventDisplayType,
  TopicSelect,
  useCreateEvent,
} from '@entities/event';
import {Topic} from '@entities/topic';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  FieldChangeHighlight as FieldHighlight,
  imageFileTypes,
  ImageUploadField,
  ObjectSelect,
  RhfDateTimePicker,
  TranslateFieldButton,
} from '@shared/components';
import {
  DATETIME_FORMAT,
  ROUTE__CATEGORY_EDIT,
  ROUTE__SUB_CATEGORY_EDIT,
  ROUTE__TOPIC_DETAILS,
} from '@shared/constants';
import {createEmptyTranslationDict, resolveEnglishText} from '@shared/helpers';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {
  dummyFunc,
  formatInTz,
  numberTransformEmptyStringToNull,
  toPickerDateInTz,
  toUtcFromTz,
} from '@shared/utils';
import {FormRow, Header} from '@widgets/common';
import {openAddTranslationsDialog} from '@widgets/common/AddTranslationsDialog';

import {COLOR__WHITE} from '@/theme/colors';

const v8nSchema = object().shape({
  name: string().required(),
  shortName: string().defined().default(''),
  description: string().defined(),
  yesText: string().defined().default(''),
  noText: string().defined().default(''),
  overview: string().defined().default(''),
  sourceOfTruth: string().defined().default(''),
  rules: string().defined().default(''),
  category: mixed<Category>()
    .required()
    .nullable()
    .notOneOf([null], 'required'),
  subCategory: mixed<SubCategory>().nullable().defined(),
  topic: mixed<Topic>().nullable().defined(),
  geos: mixed<GeoFilterOption[]>().defined().default([]),
  startDatetime: mixed<Date>().required(),
  endDatetime: mixed<Date>().required(),
  liveDatetime: mixed<Date>().nullable().defined(),
  autoPublishEnabled: mixed<boolean>().defined(),
  yesPrice: numberTransformEmptyStringToNull
    .required()
    .positive('must be positive')
    .max(0.99, 'max 0.99'),
  displayType: string().oneOf(Object.values(EventDisplayType)).required(),
  logoFile: mixed<FileWithPath>().nullable().defined(),
  shortLogoFile: mixed<FileWithPath>().nullable().defined(),
});

type FormValues = {
  name: string;
  shortName: string;
  description: string;
  yesText: string;
  noText: string;
  overview: string;
  sourceOfTruth: string;
  rules: string;
  category: Category | null;
  subCategory: SubCategory | null;
  topic: Topic | null;
  geos: GeoFilterOption[];
  startDatetime: Date | null;
  endDatetime: Date | null;
  liveDatetime: Date | null;
  autoPublishEnabled: boolean;
  yesPrice: number;
  displayType: EventDisplayType;
  logoFile: FileWithPath | null;
  shortLogoFile: FileWithPath | null;
};

const SectionTitle = ({children}: {children: ReactNode}) => (
  <Typography variant="h3bold" display="block" mb={4} mt={1}>
    {children}
  </Typography>
);

export const CreateEventPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {data: geoOptions = []} = useGetGeoFilterOptions();
  const {mutate: createEvent, isPending: isCreating} = useCreateEvent();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    () => createEmptyTranslationDict(),
  );
  const [shortNameTranslations, setShortNameTranslations] =
    useState<TranslationDict>(() => createEmptyTranslationDict());
  const [descriptionTranslations, setDescriptionTranslations] =
    useState<TranslationDict>(() => createEmptyTranslationDict());
  const [yesTextTranslations, setYesTextTranslations] =
    useState<TranslationDict>(() => createEmptyTranslationDict());
  const [noTextTranslations, setNoTextTranslations] = useState<TranslationDict>(
    () => createEmptyTranslationDict(),
  );
  const [overviewTranslations, setOverviewTranslations] =
    useState<TranslationDict>(() => createEmptyTranslationDict());
  const [sourceOfTruthTranslations, setSourceOfTruthTranslations] =
    useState<TranslationDict>(() => createEmptyTranslationDict());
  const [rulesTranslations, setRulesTranslations] = useState<TranslationDict>(
    () => createEmptyTranslationDict(),
  );

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(v8nSchema) as unknown as Resolver<FormValues>,
    defaultValues: {
      name: '',
      shortName: '',
      description: '',
      yesText: '',
      noText: '',
      overview: '',
      sourceOfTruth: '',
      rules: '',
      category: null,
      subCategory: null,
      topic: null,
      geos: [],
      startDatetime: null,
      endDatetime: null,
      liveDatetime: null,
      autoPublishEnabled: false,
      yesPrice: 0.5,
      displayType: EventDisplayType.fixedLive,
      logoFile: null,
      shortLogoFile: null,
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: {dirtyFields},
  } = formMethods;

  const currentCategory = useWatch({control, name: 'category'});
  const currentSubCategory = useWatch({control, name: 'subCategory'});
  const selectedTopic = useWatch({control, name: 'topic'});
  const startDatetime = useWatch({control, name: 'startDatetime'});
  const yesPrice = useWatch({control, name: 'yesPrice'});
  const noPrice = isNil(yesPrice) ? null : Math.max(0, 1 - Number(yesPrice));

  useUpdateEffect(() => {
    if (!isNil(currentCategory)) {
      setValue('subCategory', null);
    }
  }, [currentCategory?.id]);

  useUpdateEffect(() => {
    setValue('topic', null);
  }, [currentSubCategory?.id]);

  // When a topic is selected, prefill the event dates from the topic's dates.
  useUpdateEffect(() => {
    if (!selectedTopic) {
      return;
    }
    if (selectedTopic.startDate) {
      setValue('startDatetime', toPickerDateInTz(selectedTopic.startDate));
    }
    if (selectedTopic.endDate) {
      setValue('endDatetime', toPickerDateInTz(selectedTopic.endDate));
    }
  }, [selectedTopic?.topicId]);

  const currentName = watch('name');
  const currentShortName = watch('shortName');
  const currentDescription = watch('description');
  const currentYesText = watch('yesText');
  const currentNoText = watch('noText');
  const currentOverview = watch('overview');
  const currentSourceOfTruth = watch('sourceOfTruth');
  const currentRules = watch('rules');

  const onSubmit = (values: FormValues): void => {
    createEvent(
      {
        nameTrans: {
          ...nameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        shortNameTrans: {
          ...shortNameTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            shortNameTranslations,
            values.shortName,
            !!dirtyFields.shortName,
          ),
        },
        descriptionTrans: {
          ...descriptionTranslations,
          [SupportedLanguage.English]: resolveEnglishText(
            descriptionTranslations,
            values.description,
            !!dirtyFields.description,
          ),
        },
        startDate: values.startDatetime ?? new Date(),
        endDate: values.endDatetime ?? new Date(),
        liveDate: values.liveDatetime,
        yesPrice: values.yesPrice,
        noPrice: noPrice ?? 0,
        categoryId: values.category?.id ?? '',
        subCategorySlug:
          values.subCategory?.slug ?? values.subCategory?.id ?? '',
        topicId: values.topic?.topicId ?? null,
        parentId: null,
        isPromotionNeeded: false,
        autoPublishEnabled: values.autoPublishEnabled,
        displayType: values.displayType,
        geoSlugs: values.geos.map(g => g.id),
      },
      {
        onSuccess: () => navigate(-1),
        onError: err => {
          const msg = isAxiosError(err) ? getErrorMessageFromApiError(err) : '';
          enqueueSnackbar(`Could not create event. ${msg}`, {variant: 'error'});
        },
      },
    );
  };

  const onInvalid = () => {
    enqueueSnackbar('Please fill in all required fields', {variant: 'error'});
  };

  const translatedField = (
    fieldName: keyof FormValues,
    label: string,
    currentValue: string,
    translations: TranslationDict,
    setTranslations: (t: TranslationDict) => void,
    multiline?: boolean,
  ) => (
    <Box display="flex" alignItems="flex-start" gap={1}>
      <Controller
        name={fieldName}
        control={control}
        render={({field, fieldState}) => (
          <TextField
            {...field}
            label={label}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            multiline={multiline}
            rows={multiline ? 4 : undefined}
            size="small"
            fullWidth
          />
        )}
      />
      <TranslateFieldButton
        onClick={() => {
          void openAddTranslationsDialog({
            fieldName: label,
            initialTranslations: translations,
            enTrans: resolveEnglishText(
              translations,
              currentValue,
              !!dirtyFields[fieldName],
            ),
            isMultiLineText: multiline,
          })
            .then((t: TranslationDict) => {
              setValue(fieldName, t[SupportedLanguage.English] as never);
              setTranslations(t);
              return;
            })
            .catch(dummyFunc);
        }}
      />
    </Box>
  );

  return (
    <>
      <Header title="Create event" mb={1} />

      <CardBox bgcolor={COLOR__WHITE}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
            {/* ── INFORMATION ── */}
            <SectionTitle>Information</SectionTitle>
            <Box display="flex" gap={3} alignItems="flex-start">
              <Box flexShrink={0} width={160}>
                <ImageUploadField<FormValues>
                  fieldName="logoFile"
                  label="Icon"
                  acceptedFileTypes={imageFileTypes}
                  existingFileUrl={selectedTopic?.logoUrl ?? undefined}
                  width={160}
                  height={160}
                  alt="Event icon"
                />
                <Box mt={4}>
                  <ImageUploadField<FormValues>
                    fieldName="shortLogoFile"
                    label="Short logo"
                    acceptedFileTypes={imageFileTypes}
                    width={160}
                    height={160}
                    alt="Event short logo"
                  />
                </Box>
              </Box>

              <Stack spacing={3.5} flex={1}>
                {translatedField(
                  'name',
                  'Name',
                  currentName,
                  nameTranslations,
                  setNameTranslations,
                )}
                {translatedField(
                  'shortName',
                  'Short name',
                  currentShortName,
                  shortNameTranslations,
                  setShortNameTranslations,
                )}
                {translatedField(
                  'description',
                  'Description',
                  currentDescription,
                  descriptionTranslations,
                  setDescriptionTranslations,
                  true,
                )}

                <Box display="flex" gap={1} alignItems="center">
                  <Controller
                    name="category"
                    control={control}
                    render={({field, fieldState}) => (
                      <Box flex={1}>
                        <CategorySelect
                          label="Category"
                          error={fieldState.error?.message}
                          {...field}
                          fullWidth
                        />
                      </Box>
                    )}
                  />
                  <Tooltip title="Open category">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!currentCategory}
                        onClick={() =>
                          currentCategory &&
                          navigate(
                            ROUTE__CATEGORY_EDIT.replace(
                              ':id',
                              currentCategory.id,
                            ),
                          )
                        }
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Box display="flex" gap={1} alignItems="center">
                  <Controller
                    name="subCategory"
                    control={control}
                    render={({field, fieldState}) => (
                      <Box flex={1}>
                        <SubCategorySelect
                          label="Sub-category"
                          categoryId={currentCategory?.id ?? ''}
                          error={fieldState.error?.message}
                          {...field}
                          fullWidth
                        />
                      </Box>
                    )}
                  />
                  <Tooltip title="Open sub-category">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!currentSubCategory || !currentCategory}
                        onClick={() =>
                          currentCategory &&
                          currentSubCategory &&
                          navigate(
                            ROUTE__SUB_CATEGORY_EDIT.replace(
                              ':categoryId',
                              currentCategory.id,
                            ).replace(':id', currentSubCategory.id),
                          )
                        }
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Box display="flex" gap={1} alignItems="center">
                  <Controller
                    name="topic"
                    control={control}
                    render={({field, fieldState}) => (
                      <TopicSelect
                        label="Topic"
                        error={fieldState.error?.message}
                        categoryId={currentCategory?.id}
                        subCategoryId={currentSubCategory?.id}
                        fullWidth
                        {...field}
                      />
                    )}
                  />
                  <Tooltip title="Open topic">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!selectedTopic}
                        onClick={() =>
                          selectedTopic &&
                          navigate(
                            ROUTE__TOPIC_DETAILS.replace(
                              ':topic_id',
                              selectedTopic.topicId,
                            ),
                          )
                        }
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Controller
                  name="geos"
                  control={control}
                  render={({field}) => {
                    return (
                      <ObjectSelect<GeoFilterOption>
                        label="GEO"
                        multiple
                        options={geoOptions}
                        labelProp="name"
                        valueProp="id"
                        value={field.value ?? []}
                        onChange={val => field.onChange(val ?? [])}
                        renderOption={opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {opt.logo_url && (
                                <img
                                  src={opt.logo_url}
                                  alt=""
                                  width="auto"
                                  height={20}
                                  style={{flexShrink: 0}}
                                />
                              )}
                              <Typography>{opt.name || opt.id}</Typography>
                            </Box>
                          </MenuItem>
                        )}
                        renderValue={selected => (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {(selected as string[]).map(id => {
                              const opt = geoOptions.find(o => o.id === id);
                              return (
                                <Chip
                                  key={id}
                                  size="small"
                                  label={opt ? opt.name || opt.id : id}
                                />
                              );
                            })}
                          </Box>
                        )}
                      />
                    );
                  }}
                />
              </Stack>
            </Box>

            <Divider sx={{my: 3}} />

            {/* ── PARAMS ── */}
            <SectionTitle>Params</SectionTitle>
            <Stack spacing={3.5}>
              <Stack spacing={0.5}>
                <FormRow>
                  <RhfDateTimePicker
                    name="startDatetime"
                    control={control}
                    label="Start date & time"
                    digitalClock
                  />
                  <RhfDateTimePicker
                    name="liveDatetime"
                    control={control}
                    label="Live date & time"
                    digitalClock
                  />
                  <RhfDateTimePicker
                    name="endDatetime"
                    control={control}
                    label="End date & time"
                    digitalClock
                  />
                </FormRow>

                <Controller
                  name="autoPublishEnabled"
                  control={control}
                  render={({field}) => (
                    <FieldHighlight changed={!!dirtyFields.autoPublishEnabled}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(_, checked) => field.onChange(checked)}
                          />
                        }
                        label={`Auto publish event at ${
                          startDatetime
                            ? formatInTz(
                                toUtcFromTz(startDatetime),
                                DATETIME_FORMAT,
                              )
                            : 'start date'
                        }`}
                      />
                    </FieldHighlight>
                  )}
                />
              </Stack>

              <Controller
                name="displayType"
                control={control}
                render={({field}) => (
                  <ObjectSelect<{value: EventDisplayType; label: string}>
                    label="Display type"
                    options={Object.values(EventDisplayType).map(t => ({
                      value: t,
                      label: formatEventDisplayType(t),
                    }))}
                    labelProp="label"
                    valueProp="value"
                    value={
                      field.value
                        ? {
                            value: field.value,
                            label: formatEventDisplayType(field.value),
                          }
                        : null
                    }
                    onChange={opt => {
                      if (!Array.isArray(opt) && opt !== null)
                        field.onChange(opt.value);
                    }}
                    sx={{maxWidth: 300}}
                  />
                )}
              />

              <Box display="flex" alignItems="center" gap={1}>
                <Controller
                  name="yesPrice"
                  control={control}
                  render={({field, fieldState}) => (
                    <TextField
                      {...field}
                      label="Yes price"
                      type="number"
                      size="small"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      slotProps={{htmlInput: {min: 0, max: 0.99, step: 0.01}}}
                      sx={{width: 160}}
                    />
                  )}
                />
              </Box>
            </Stack>

            <Divider sx={{my: 3}} />

            {/* ── ADDITIONAL ── */}
            <SectionTitle>Additional</SectionTitle>
            <Stack spacing={3.5}>
              {translatedField(
                'yesText',
                'Yes text',
                currentYesText,
                yesTextTranslations,
                setYesTextTranslations,
              )}
              {translatedField(
                'noText',
                'No text',
                currentNoText,
                noTextTranslations,
                setNoTextTranslations,
              )}
              {translatedField(
                'overview',
                'Overview',
                currentOverview,
                overviewTranslations,
                setOverviewTranslations,
                true,
              )}
              {translatedField(
                'sourceOfTruth',
                'Source of truth',
                currentSourceOfTruth,
                sourceOfTruthTranslations,
                setSourceOfTruthTranslations,
                true,
              )}
              {translatedField(
                'rules',
                'Rules',
                currentRules,
                rulesTranslations,
                setRulesTranslations,
                true,
              )}
            </Stack>

            <FormRow width={420} mt={4}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
              >
                Back
              </Button>
              <Button type="submit" sx={{flex: 2}} disabled={isCreating}>
                Create event
              </Button>
            </FormRow>
          </form>
        </FormProvider>
      </CardBox>
    </>
  );
};
